import React, { useEffect, useState } from 'react';
import { Vault, TrendingUp, Shield, Calendar } from 'lucide-react';
import { Panel } from '../../layout/Panel';
import { DataSourceBadge, DATA_SOURCES } from '../../ui/DataSourceBadge';
import { cbuApi } from '../../../services/api';
import type { ReservesData } from '../../../services/api/cbuApi';

interface ReservesPanelProps {
  isMinimized?: boolean;
  onMinimize?: () => void;
}

export const ReservesPanel: React.FC<ReservesPanelProps> = ({
  isMinimized,
  onMinimize,
}) => {
  const [data, setData] = useState<ReservesData | null>(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    const fetchData = async () => {
      const response = await cbuApi.getReserves();
      if (response.data) {
        setData(response.data);
      }
      setLoading(false);
    };
    fetchData();
  }, []);

  // IMF adequacy metric (3 months minimum)
  const adequacyLevel = data ? (data.monthsImport >= 6 ? 'strong' : data.monthsImport >= 3 ? 'adequate' : 'low') : null;

  const exportData = data ? [{
    totalReserves: data.totalReserves,
    monthsImport: data.monthsImport,
    lastUpdated: data.lastUpdated,
    source: data.source,
  }] : [];

  return (
    <Panel
      id="reserves"
      title="International Reserves"
      subtitle="CBU"
      status="static"
      isMinimized={isMinimized}
      onMinimize={onMinimize}
      data={exportData}
    >
      <div className="h-full flex flex-col p-3">
        {loading ? (
          <div className="flex-1 flex items-center justify-center text-gray-500 text-sm">
            Loading reserves data...
          </div>
        ) : data ? (
          <>
            {/* HERO - Giant Reserves Number */}
            <div className="text-center py-6 px-4 bg-gradient-to-b from-data-amber/10 to-transparent rounded-lg border border-data-amber/20 mb-4">
              <div className="flex items-center justify-center gap-2 mb-3">
                <Vault size={18} className="text-data-amber" />
                <span className="text-xs text-data-amber/80 uppercase tracking-widest font-semibold">
                  Total International Reserves
                </span>
              </div>

              {/* GIANT Number */}
              <div className="text-5xl md:text-6xl font-mono font-black text-data-amber tracking-tight">
                <span className="text-3xl md:text-4xl align-top">$</span>
                {data.totalReserves}
                <span className="text-3xl md:text-4xl">B</span>
              </div>

              {/* Growth Badge */}
              <div className="mt-4 inline-flex items-center gap-2 px-3 py-1.5 bg-data-green/10 rounded-full border border-data-green/20">
                <TrendingUp size={14} className="text-data-green" />
                <span className="text-sm font-semibold text-data-green">+21% YoY</span>
                <span className="text-xs text-gray-500">from $41B</span>
              </div>
            </div>

            {/* Key Metrics - 2 Column */}
            <div className="grid grid-cols-2 gap-3 mb-3">
              {/* Import Cover */}
              <div className="p-3 bg-terminal-border/40 rounded-lg">
                <div className="flex items-center gap-1.5 mb-2">
                  <Calendar size={12} className="text-data-blue" />
                  <span className="text-xs text-gray-400 uppercase tracking-wider">Import Cover</span>
                </div>
                <div className="flex items-baseline gap-1">
                  <span className="text-3xl font-mono font-black text-white">
                    {data.monthsImport}
                  </span>
                  <span className="text-lg text-gray-400">mo</span>
                </div>
                <div className={`mt-2 text-xs px-2 py-0.5 rounded-full inline-block ${
                  adequacyLevel === 'strong'
                    ? 'bg-data-green/20 text-data-green'
                    : 'bg-data-amber/20 text-data-amber'
                }`}>
                  {adequacyLevel === 'strong' ? 'Excellent' : 'Adequate'}
                </div>
              </div>

              {/* % of GDP */}
              <div className="p-3 bg-terminal-border/40 rounded-lg">
                <div className="flex items-center gap-1.5 mb-2">
                  <Shield size={12} className="text-data-green" />
                  <span className="text-xs text-gray-400 uppercase tracking-wider">% of GDP</span>
                </div>
                <div className="flex items-baseline gap-1">
                  <span className="text-3xl font-mono font-black text-white">
                    ~43
                  </span>
                  <span className="text-lg text-gray-400">%</span>
                </div>
                <div className="mt-2 text-xs px-2 py-0.5 rounded-full inline-block bg-data-green/20 text-data-green">
                  Strong Buffer
                </div>
              </div>
            </div>

            {/* IMF Adequacy Visual */}
            <div className="p-3 bg-terminal-border/20 rounded-lg">
              <div className="text-xs text-gray-500 mb-2">IMF Adequacy (3 months minimum)</div>
              <div className="relative h-4 bg-terminal-border rounded-full overflow-hidden">
                {/* Progress fill */}
                <div
                  className={`h-full rounded-full transition-all ${
                    adequacyLevel === 'strong' ? 'bg-data-green' : 'bg-data-amber'
                  }`}
                  style={{ width: `${Math.min((data.monthsImport / 12) * 100, 100)}%` }}
                />
                {/* IMF minimum marker */}
                <div
                  className="absolute top-0 bottom-0 w-0.5 bg-white/50"
                  style={{ left: '25%' }}
                />
              </div>
              <div className="flex justify-between mt-1.5 text-xs">
                <span className="text-gray-600">0</span>
                <span className="text-white/50">3 (IMF min)</span>
                <span className="text-data-green">Current: {data.monthsImport}</span>
                <span className="text-gray-600">12</span>
              </div>
            </div>
          </>
        ) : null}

        {/* Footer */}
        <div className="mt-auto pt-2 border-t border-terminal-border/50 flex items-center justify-between">
          <DataSourceBadge source={DATA_SOURCES.CBU} showLabel size="xs" />
          <span className="text-xs text-gray-500">{data?.lastUpdated}</span>
        </div>
      </div>
    </Panel>
  );
};

export default ReservesPanel;
